import { User, Campaign } from '../types';

export class CampaignFilterService {
  /**
   * Get campaigns that a user can access based on their role and assigned campaigns
   * @param user - The user object
   * @param allCampaigns - All available campaigns
   * @returns Array of campaigns the user can access
   */
  static getUserAccessibleCampaigns(user: User, allCampaigns: Campaign[]): Campaign[] {
    // Super users, administrators, and managers can see all campaigns by default
    if (user.role === 'super_user' || user.role === 'administrator' || user.role === 'manager') {
      return allCampaigns;
    }

    // For Team Lead and Supervisor roles, check assigned campaigns (+ Training when present)
    if (user.role === 'team_lead' || user.role === 'supervisor') {
      if (!user.assignedCampaigns) {
        return []; // No campaigns assigned
      }

      try {
        const assignedCampaignIds = JSON.parse(user.assignedCampaigns);
        const accessible = allCampaigns.filter(campaign => assignedCampaignIds.includes(campaign.id));
        const training = allCampaigns.find(
          (c) => String(c.name || '').trim().toLowerCase() === 'training'
        );
        if (training && !accessible.some((c) => c.id === training.id)) {
          return [...accessible, training];
        }
        return accessible;
      } catch (error) {
        console.error('Error parsing assigned campaigns:', error);
        return [];
      }
    }

    // Agents and other roles don't need campaign filtering for reports
    // They can see all campaigns (this might need to be adjusted based on business requirements)
    return allCampaigns;
  }

  /**
   * Check if a user can access a specific campaign
   * @param user - The user object
   * @param campaignId - The campaign ID to check
   * @param allCampaigns - All available campaigns
   * @returns Boolean indicating if user can access the campaign
   */
  static canUserAccessCampaign(user: User, campaignId: string, allCampaigns: Campaign[]): boolean {
    const accessibleCampaigns = this.getUserAccessibleCampaigns(user, allCampaigns);
    return accessibleCampaigns.some(campaign => campaign.id === campaignId);
  }

  /**
   * Get campaign IDs that a user can access
   * @param user - The user object
   * @param allCampaigns - All available campaigns
   * @returns Array of campaign IDs the user can access
   */
  static getUserAccessibleCampaignIds(user: User, allCampaigns: Campaign[]): string[] {
    const accessibleCampaigns = this.getUserAccessibleCampaigns(user, allCampaigns);
    return accessibleCampaigns.map(campaign => campaign.id);
  }

  /**
   * Filter users based on campaign access
   * @param users - Array of users to filter
   * @param campaignId - The campaign ID to filter by
   * @param allCampaigns - All available campaigns
   * @returns Array of users who can access the specified campaign
   */
  static filterUsersByCampaignAccess(users: User[], campaignId: string, allCampaigns: Campaign[]): User[] {
    return users.filter(user => this.canUserAccessCampaign(user, campaignId, allCampaigns));
  }

  /**
   * Get campaign filter options for a user (for dropdowns, etc.)
   * @param user - The user object
   * @param allCampaigns - All available campaigns
   * @returns Array of campaign options for filtering
   */
  static getCampaignFilterOptions(user: User, allCampaigns: Campaign[]): Array<{ value: string; label: string }> {
    const accessibleCampaigns = this.getUserAccessibleCampaigns(user, allCampaigns);
    return accessibleCampaigns.map(campaign => ({
      value: campaign.id,
      label: campaign.name
    }));
  }

  /**
   * Check if campaign selection should be shown for a role
   * @param role - The user role
   * @returns Boolean indicating if campaign selection should be shown
   */
  static shouldShowCampaignSelection(role: string): boolean {
    return ['team_lead', 'supervisor'].includes(role);
  }

  /**
   * Get default campaign assignment for a role
   * @param role - The user role
   * @returns Default campaign assignment (empty array for agents, null for managers and above)
   */
  static getDefaultCampaignAssignment(role: string): string[] | null {
    if (role === 'super_user' || role === 'administrator' || role === 'manager') {
      return null; // Can see all campaigns
    }
    if (role === 'agent' || role === 'other') {
      return []; // No campaign assignments needed
    }
    return []; // Team lead and supervisor start with no assignments
  }
}
